import { useEffect, useRef, useState } from 'react';

import { fetchPoiPipelines } from '@/api/poiPipelinesApi';
import type { PoiPipelinesResponse } from '@/api/poiPipelinesApi';
import { showErrorToast } from '@/errors/showErrorToast';

export interface PoiPipelinesState {
    data: PoiPipelinesResponse | null;
    isLoading: boolean;
    isError: boolean;
}

const IDLE: PoiPipelinesState = { data: null, isLoading: false, isError: false };

/**
 * Fetches the associated-pipelines breakdown for the selected POI.
 *
 * Unlike the History tab's lazy fetch, this fires as soon as a POI is selected: the map
 * needs the radius circle and clipped sections immediately, and the Pipelines tab then
 * renders the same response without a second request. Results are memoised in a ref-held
 * `Map` keyed by client + POI, so re-selecting a POI is served from cache with no refetch
 * and no flicker. The in-flight request is aborted when the selection changes, so a stale
 * response can never overwrite a newer one.
 *
 * `retry` bypasses the cache for the current POI and refetches — surfaced as the
 * "Try Again" action in the tab's error state.
 */
export function usePoiPipelines(
    poiId: string | null,
    clientId: string | null,
    enabled: boolean,
): PoiPipelinesState & { retry: () => void } {
    const [state, setState] = useState<PoiPipelinesState>(IDLE);
    const [attempt, setAttempt] = useState(0);
    const cacheRef = useRef<Map<string, PoiPipelinesResponse>>(new Map());

    useEffect(() => {
        if (!enabled || poiId == null || !clientId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
            setState(IDLE);
            return;
        }

        const cacheKey = `${clientId}:${poiId}`;
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
            setState({ data: cached, isLoading: false, isError: false });
            return;
        }

        const controller = new AbortController();
        // eslint-disable-next-line react-x/set-state-in-effect
        setState({ data: null, isLoading: true, isError: false });

        fetchPoiPipelines(clientId, poiId, controller.signal)
            .then((data) => {
                cacheRef.current.set(cacheKey, data);
                setState({ data, isLoading: false, isError: false });
            })
            .catch((error: unknown) => {
                // The selection moved on (or unmounted) — discard this stale result silently.
                if (controller.signal.aborted) return;
                showErrorToast(error);
                setState({ data: null, isLoading: false, isError: true });
            });

        return () => {
            controller.abort();
        };
    }, [enabled, poiId, clientId, attempt]);

    // Evicting the cache entry (a no-op after a failure, which caches nothing) and bumping
    // `attempt` re-runs the effect for the CURRENT poi only — other POIs' cached results
    // are untouched, and a later switch back to them still serves from cache.
    const retry = () => {
        if (poiId != null && clientId) cacheRef.current.delete(`${clientId}:${poiId}`);
        setAttempt((n) => n + 1);
    };

    return { ...state, retry };
}
